// Application Components
import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { format } from 'date-fns'
import {
    subtractValuesAdditionalArrayForm,
    subtractValuesAdditionalConditionalForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateSubFormData,
    updateSubFormValidity,
    updateSubFormAuxiliary,
    deleteSubFormData,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import AdditionalDisciplineHistoryDeniedComponent from '../../components/generic-components/additional-discipline-history-denied-component'
import AddFormComponent from '../../components/navigation-components/add-form-component'
import FileUploadComponent from '../../components/file-upload-components/file-upload-component'

//// Carbon Components
import {
    RadioButtonGroup,
    RadioButton,
    TextInput,
    TextArea,
    Select,
    SelectItem,
    DatePicker,
    DatePickerInput,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const DisciplineHistoryDeniedForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()

    //////// State

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const fields = settingsData?.['form']?.['fields']
    const digital_sub_name = 'denied'
    const max_form_iterations =
        settingsData?.['application']?.['max_form_iterations']
    const conditions = settingsData?.['form']?.['conditions']

    //////// Search Params

    //////// Methods

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        // Update Validation State
        if (digital_name && event_name) {
            dispatch(
                updateSubFormValidity({
                    digital_name: digital_name,
                    digital_sub_name: digital_sub_name,
                    errors: {
                        [event_name]: false,
                        [event_name + 'ErrMsg']: '',
                    },
                })
            )
        }
    }

    //////// Functional Methods

    // Handle Removal Of Discipline History Denied Form Component/s
    const handleSubtractAdditionalDisciplineHistoryForm = async (e) => {
        try {
            if (e?.target?.name) {
                const addtForms = [
                    ...formsData[digital_name][digital_sub_name].auxiliary
                        .addtForms,
                ]
                const subt = parseInt(e.target.name)
                const index = addtForms?.indexOf(subt)
                if (index > -1) {
                    addtForms.splice(index, 1)
                    dispatch(
                        updateSubFormAuxiliary({
                            digital_name: digital_name,
                            digital_sub_name: digital_sub_name,
                            auxiliary: {
                                addtForms: addtForms,
                            },
                        })
                    )
                    const values = subtractValuesAdditionalArrayForm(
                        fields,
                        max_form_iterations,
                        formsData?.[digital_name]?.[digital_sub_name]?.values,
                        index
                    )
                    if (values) {
                        dispatch(
                            deleteSubFormData({
                                digital_name: digital_name,
                                digital_sub_name: digital_sub_name,
                            })
                        )
                        dispatch(
                            updateSubFormData({
                                digital_name: digital_name,
                                digital_sub_name: digital_sub_name,
                                values: values,
                            })
                        )
                    }
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Discipline History Denied Component Error:',
                error
            )
            return null
        }
    }

    // Handle Removal Of Additional Discipline History Denied Component/s
    const handleSubtractAdditionalConditionalComponent = (e) => {
        try {
            if (e?.target?.name && e?.target?.value) {
                const values = subtractValuesAdditionalConditionalForm(
                    conditions,
                    formsData?.[digital_name]?.[digital_sub_name]?.values,
                    e?.target?.name,
                    e?.target?.value
                )
                if (values) {
                    dispatch(
                        deleteSubFormData({
                            digital_name: digital_name,
                            digital_sub_name: digital_sub_name,
                        })
                    )
                    dispatch(
                        updateSubFormAuxiliary({
                            digital_name: digital_name,
                            digital_sub_name: digital_sub_name,
                            auxiliary: {
                                addtForms: [],
                            },
                        })
                    )
                    dispatch(
                        updateSubFormData({
                            digital_name: digital_name,
                            digital_sub_name: digital_sub_name,
                            values: values,
                        })
                    )
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Discipline History Denied Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields
    const handleData = (e, id) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateSubFormData({
                    digital_name: digital_name,
                    digital_sub_name: digital_sub_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateSubFormData({
                    digital_name: digital_name,
                    digital_sub_name: digital_sub_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        } else if (e?.length > 0 && id) {
            dispatch(
                updateSubFormData({
                    digital_name: digital_name,
                    digital_sub_name: digital_sub_name,
                    values: {
                        [id]: format(new Date(e?.[0]), 'MM/dd/yyyy'),
                    },
                })
            )
            revalidation(id)
        }
        //// Start Watson Assistant Journey

        if (e?.target?.name === 'RSD82A' && e?.target?.value === 'Y') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Discipline History Denied Healthcare Yes Tour'
            )
        } else {
            instance.tours.endTour()
        }

        //// End Watson Assistant Journey
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (
        formsData?.[digital_name]?.[digital_sub_name]?.values?.RSD82A ===
            undefined ||
        formsData?.[digital_name]?.[digital_sub_name]?.values?.RSD82A === 'N'
    ) {
        return (
            <>
                <RadioButtonGroup
                    className={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82A
                            ? 'required invalid'
                            : 'required'
                    }
                    defaultSelected={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD82A
                    }
                    valueSelected={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD82A
                    }
                    legendText={`Have you ever been denied or is there now any proceeding to deny your application for any health care license to practice in Florida or any other state, jurisdiction, or country?`}
                    name="RSD82A"
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82AErrMsg
                    }
                >
                    <RadioButton
                        id="RSD82AYes"
                        name="RSD82AYes"
                        labelText="Yes"
                        value="Y"
                        type="radio"
                        onClick={(e) => {
                            handleData(e)
                            handleSubtractAdditionalConditionalComponent(e)
                        }}
                    />
                    <RadioButton
                        id="RSD82ANo"
                        name="RSD82ANo"
                        labelText="No"
                        value="N"
                        type="radio"
                        onClick={(e) => {
                            handleData(e)
                            handleSubtractAdditionalConditionalComponent(e)
                        }}
                    />
                </RadioButtonGroup>
            </>
        )
    } else if (
        formsData?.[digital_name]?.[digital_sub_name]?.values?.RSD82A === 'Y' &&
        formsData?.[digital_name]?.[digital_sub_name]?.auxiliary?.addtForms
            ?.length === 0
    ) {
        return (
            <>
                <RadioButtonGroup
                    className={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82A
                            ? 'required invalid'
                            : 'required'
                    }
                    defaultSelected={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD82A
                    }
                    valueSelected={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD82A
                    }
                    legendText={`Have you ever been denied or is there now any proceeding to deny your application for any health care license to practice in Florida or any other state, jurisdiction, or country?`}
                    name="RSD82A"
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82AErrMsg
                    }
                >
                    <RadioButton
                        id="RSD82AYes"
                        name="RSD82AYes"
                        labelText="Yes"
                        value="Y"
                        type="radio"
                        onClick={(e) => {
                            handleData(e)
                            handleSubtractAdditionalConditionalComponent(e)
                        }}
                    />
                    <RadioButton
                        id="RSD82ANo"
                        name="RSD82ANo"
                        labelText="No"
                        value="N"
                        type="radio"
                        onClick={(e) => {
                            handleData(e)
                            handleSubtractAdditionalConditionalComponent(e)
                        }}
                    />
                </RadioButtonGroup>

                <TextInput
                    className="required"
                    labelText="Name of Agency:"
                    id="RSD34A"
                    name="RSD34A"
                    placeholder=""
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD34A
                    }
                    maxLength="60"
                    type="text"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD34A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD34AErrMsg
                    }
                />

                <Select
                    className="required"
                    defaultValue={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.STATEA
                    }
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.STATEA
                    }
                    helper=""
                    labelText="State:"
                    id="STATEA"
                    name="STATEA"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.STATEA
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.STATEAErrMsg
                    }
                >
                    <SelectItem text="Choose an option" value="" />
                    <SelectItem text="Alabama" value="AL" />
                    <SelectItem text="Alaska" value="AK" />
                    <SelectItem text="American Samoa" value="AS" />
                    <SelectItem text="Arizona" value="AZ" />
                    <SelectItem text="Arkansas" value="AR" />
                    <SelectItem text="California" value="CA" />
                    <SelectItem text="Colorado" value="CO" />
                    <SelectItem text="Connecticut" value="CT" />
                    <SelectItem text="Delaware" value="DE" />
                    <SelectItem text="District of Columbia" value="DC" />
                    <SelectItem text="F. S. of Micronesia" value="FM" />
                    <SelectItem text="Florida" value="FL" />
                    <SelectItem text="Georgia" value="GA" />
                    <SelectItem text="Guam" value="GU" />
                    <SelectItem text="Hawaii" value="HI" />
                    <SelectItem text="Idaho" value="ID" />
                    <SelectItem text="Illinois" value="IL" />
                    <SelectItem text="Indiana" value="IN" />
                    <SelectItem text="Iowa" value="IA" />
                    <SelectItem text="Kansas" value="KS" />
                    <SelectItem text="Kentucky" value="KY" />
                    <SelectItem text="Louisiana" value="LA" />
                    <SelectItem text="Maine" value="ME" />
                    <SelectItem text="Marshall Islands" value="MH" />
                    <SelectItem text="Maryland" value="MD" />
                    <SelectItem text="Massachusetts" value="MA" />
                    <SelectItem text="Michigan" value="MI" />
                    <SelectItem text="Military A" value="AA" />
                    <SelectItem text="Military E" value="AE" />
                    <SelectItem text="Military P" value="AP" />
                    <SelectItem text="Minnesota" value="MN" />
                    <SelectItem text="Mississippi" value="MS" />
                    <SelectItem text="Missouri" value="MO" />
                    <SelectItem text="Montana" value="MT" />
                    <SelectItem text="N. Carolina" value="NC" />
                    <SelectItem text="N. Dakota" value="ND" />
                    <SelectItem text="N. Marianas" value="MP" />
                    <SelectItem text="Nebraska" value="NE" />
                    <SelectItem text="Nevada" value="NV" />
                    <SelectItem text="New Hampshire" value="NH" />
                    <SelectItem text="New Jersey" value="NJ" />
                    <SelectItem text="New Mexico" value="NM" />
                    <SelectItem text="New York" value="NY" />
                    <SelectItem text="Ohio" value="OH" />
                    <SelectItem text="Oklahoma" value="OK" />
                    <SelectItem text="Oregon" value="OR" />
                    <SelectItem text="OUT OF STATE" value="ZZ" />
                    <SelectItem text="Palau" value="PW" />
                    <SelectItem text="Pennsylvania" value="PA" />
                    <SelectItem text="Puerto Rico" value="PR" />
                    <SelectItem text="Rhode Island" value="RI" />
                    <SelectItem text="S. Carolina" value="SC" />
                    <SelectItem text="S. Dakota" value="SD" />
                    <SelectItem text="Tennessee" value="TN" />
                    <SelectItem text="Texas" value="TX" />
                    <SelectItem text="Utah" value="UT" />
                    <SelectItem text="Vermont" value="VT" />
                    <SelectItem text="Virgin Islands" value="VI" />
                    <SelectItem text="Virginia" value="VA" />
                    <SelectItem text="Washington" value="WA" />
                    <SelectItem text="West Virginia" value="WV" />
                    <SelectItem text="Wisconsin" value="WI" />
                    <SelectItem text="Wyoming" value="WY" />
                </Select>

                <DatePicker
                    className="required"
                    dateFormat="m/d/Y"
                    datePickerType="single"
                    onChange={(e) => handleData(e, 'RSD36A')}
                    onClose={(e) => handleData(e, 'RSD36A')}
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD36A
                    }
                >
                    <DatePickerInput
                        className="required"
                        labelText="Action Date:"
                        id="RSD36A"
                        name="RSD36A"
                        placeholder="MM/DD/YYYY"
                        type="text"
                        invalid={
                            formsData?.[digital_name]?.[digital_sub_name]
                                ?.errors?.RSD36A
                        }
                        invalidText={
                            formsData?.[digital_name]?.[digital_sub_name]
                                ?.errors?.RSD36AErrMsg
                        }
                    />
                </DatePicker>

                <TextInput
                    className="required"
                    labelText="Final Action:"
                    id="FIN_ACTA"
                    name="FIN_ACTA"
                    placeholder=""
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.FIN_ACTA
                    }
                    maxLength="60"
                    type="text"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.FIN_ACTA
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.FIN_ACTAErrMsg
                    }
                />

                <Select
                    className="required"
                    defaultValue={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD39A
                    }
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD39A
                    }
                    helper=""
                    labelText="Under Appeal:"
                    id="RSD39A"
                    name="RSD39A"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD39A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD39AErrMsg
                    }
                >
                    <SelectItem text="Choose an option" value="" />
                    <SelectItem text="Yes" value="Y" />
                    <SelectItem text="No" value="N" />
                    <SelectItem text="No Answer" value="NA" />
                </Select>

                <TextArea
                    className="required"
                    labelText="Applicant Statement:"
                    id="LIC_NTSA"
                    name="LIC_NTSA"
                    placeholder=""
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.LIC_NTSA
                    }
                    maxLength="4000"
                    type="textarea"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.LIC_NTSA
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.LIC_NTSAErrMsg
                    }
                />

                <AddFormComponent
                    digital_sub_name={digital_sub_name}
                    sub_title={`Add Additional Discipline  (Denied):`}
                />

                <div className="file-upload-group">
                    <div className="file-upload-group-item">
                        <FileUploadComponent />
                    </div>
                </div>
            </>
        )
    } else if (
        formsData?.[digital_name]?.[digital_sub_name]?.values?.RSD82A === 'Y' &&
        formsData?.[digital_name]?.[digital_sub_name]?.auxiliary?.addtForms
            ?.length > 0
    ) {
        return (
            <>
                <RadioButtonGroup
                    className={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82A
                            ? 'required invalid'
                            : 'required'
                    }
                    defaultSelected={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD82A
                    }
                    valueSelected={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD82A
                    }
                    legendText={`Have you ever been denied or is there now any proceeding to deny your application for any health care license to practice in Florida or any other state, jurisdiction, or country?`}
                    name="RSD82A"
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD82AErrMsg
                    }
                >
                    <RadioButton
                        id="RSD82AYes"
                        name="RSD82AYes"
                        labelText="Yes"
                        value="Y"
                        type="radio"
                        onClick={(e) => {
                            handleData(e)
                            handleSubtractAdditionalConditionalComponent(e)
                        }}
                    />
                    <RadioButton
                        id="RSD82ANo"
                        name="RSD82ANo"
                        labelText="No"
                        value="N"
                        type="radio"
                        onClick={(e) => {
                            handleData(e)
                            handleSubtractAdditionalConditionalComponent(e)
                        }}
                    />
                </RadioButtonGroup>

                <TextInput
                    className="required"
                    labelText="Name of Agency:"
                    id="RSD34A"
                    name="RSD34A"
                    placeholder=""
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD34A
                    }
                    maxLength="60"
                    type="text"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD34A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD34AErrMsg
                    }
                />

                <Select
                    className="required"
                    defaultValue={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.STATEA
                    }
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.STATEA
                    }
                    helper=""
                    labelText="State:"
                    id="STATEA"
                    name="STATEA"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.STATEA
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.STATEAErrMsg
                    }
                >
                    <SelectItem text="Choose an option" value="" />
                    <SelectItem text="Alabama" value="AL" />
                    <SelectItem text="Alaska" value="AK" />
                    <SelectItem text="American Samoa" value="AS" />
                    <SelectItem text="Arizona" value="AZ" />
                    <SelectItem text="Arkansas" value="AR" />
                    <SelectItem text="California" value="CA" />
                    <SelectItem text="Colorado" value="CO" />
                    <SelectItem text="Connecticut" value="CT" />
                    <SelectItem text="Delaware" value="DE" />
                    <SelectItem text="District of Columbia" value="DC" />
                    <SelectItem text="F. S. of Micronesia" value="FM" />
                    <SelectItem text="Florida" value="FL" />
                    <SelectItem text="Georgia" value="GA" />
                    <SelectItem text="Guam" value="GU" />
                    <SelectItem text="Hawaii" value="HI" />
                    <SelectItem text="Idaho" value="ID" />
                    <SelectItem text="Illinois" value="IL" />
                    <SelectItem text="Indiana" value="IN" />
                    <SelectItem text="Iowa" value="IA" />
                    <SelectItem text="Kansas" value="KS" />
                    <SelectItem text="Kentucky" value="KY" />
                    <SelectItem text="Louisiana" value="LA" />
                    <SelectItem text="Maine" value="ME" />
                    <SelectItem text="Marshall Islands" value="MH" />
                    <SelectItem text="Maryland" value="MD" />
                    <SelectItem text="Massachusetts" value="MA" />
                    <SelectItem text="Michigan" value="MI" />
                    <SelectItem text="Military A" value="AA" />
                    <SelectItem text="Military E" value="AE" />
                    <SelectItem text="Military P" value="AP" />
                    <SelectItem text="Minnesota" value="MN" />
                    <SelectItem text="Mississippi" value="MS" />
                    <SelectItem text="Missouri" value="MO" />
                    <SelectItem text="Montana" value="MT" />
                    <SelectItem text="N. Carolina" value="NC" />
                    <SelectItem text="N. Dakota" value="ND" />
                    <SelectItem text="N. Marianas" value="MP" />
                    <SelectItem text="Nebraska" value="NE" />
                    <SelectItem text="Nevada" value="NV" />
                    <SelectItem text="New Hampshire" value="NH" />
                    <SelectItem text="New Jersey" value="NJ" />
                    <SelectItem text="New Mexico" value="NM" />
                    <SelectItem text="New York" value="NY" />
                    <SelectItem text="Ohio" value="OH" />
                    <SelectItem text="Oklahoma" value="OK" />
                    <SelectItem text="Oregon" value="OR" />
                    <SelectItem text="OUT OF STATE" value="ZZ" />
                    <SelectItem text="Palau" value="PW" />
                    <SelectItem text="Pennsylvania" value="PA" />
                    <SelectItem text="Puerto Rico" value="PR" />
                    <SelectItem text="Rhode Island" value="RI" />
                    <SelectItem text="S. Carolina" value="SC" />
                    <SelectItem text="S. Dakota" value="SD" />
                    <SelectItem text="Tennessee" value="TN" />
                    <SelectItem text="Texas" value="TX" />
                    <SelectItem text="Utah" value="UT" />
                    <SelectItem text="Vermont" value="VT" />
                    <SelectItem text="Virgin Islands" value="VI" />
                    <SelectItem text="Virginia" value="VA" />
                    <SelectItem text="Washington" value="WA" />
                    <SelectItem text="West Virginia" value="WV" />
                    <SelectItem text="Wisconsin" value="WI" />
                    <SelectItem text="Wyoming" value="WY" />
                </Select>

                <DatePicker
                    className="required"
                    dateFormat="m/d/Y"
                    datePickerType="single"
                    onChange={(e) => handleData(e, 'RSD36A')}
                    onClose={(e) => handleData(e, 'RSD36A')}
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD36A
                    }
                >
                    <DatePickerInput
                        className="required"
                        labelText="Action Date:"
                        id="RSD36A"
                        name="RSD36A"
                        placeholder="MM/DD/YYYY"
                        type="text"
                        invalid={
                            formsData?.[digital_name]?.[digital_sub_name]
                                ?.errors?.RSD36A
                        }
                        invalidText={
                            formsData?.[digital_name]?.[digital_sub_name]
                                ?.errors?.RSD36AErrMsg
                        }
                    />
                </DatePicker>

                <TextInput
                    className="required"
                    labelText="Final Action:"
                    id="FIN_ACTA"
                    name="FIN_ACTA"
                    placeholder=""
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.FIN_ACTA
                    }
                    maxLength="60"
                    type="text"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.FIN_ACTA
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.FIN_ACTErrMsg
                    }
                />

                <Select
                    className="required"
                    defaultValue={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD39A
                    }
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.RSD39A
                    }
                    helper=""
                    labelText="Under Appeal:"
                    id="RSD39A"
                    name="RSD39A"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD39A
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.RSD39AErrMsg
                    }
                >
                    <SelectItem text="Choose an option" value="" />
                    <SelectItem text="Yes" value="Y" />
                    <SelectItem text="No" value="N" />
                    <SelectItem text="No Answer" value="NA" />
                </Select>

                <TextArea
                    className="required"
                    labelText="Applicant Statement:"
                    id="LIC_NTSA"
                    name="LIC_NTSA"
                    placeholder=""
                    value={
                        formsData?.[digital_name]?.[digital_sub_name]?.values
                            ?.LIC_NTSA
                    }
                    maxLength="4000"
                    type="textarea"
                    onChange={handleData}
                    invalid={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.LIC_NTSA
                    }
                    invalidText={
                        formsData?.[digital_name]?.[digital_sub_name]?.errors
                            ?.LIC_NTSAErrMsg
                    }
                />

                {formsData?.[digital_name]?.[
                    digital_sub_name
                ]?.auxiliary?.addtForms.map((el) => (
                    <AdditionalDisciplineHistoryDeniedComponent
                        key={el}
                        number={el}
                        handleData={handleData}
                        handleSubtractAdditionalDisciplineHistoryForm={
                            handleSubtractAdditionalDisciplineHistoryForm
                        }
                    />
                ))}

                <AddFormComponent
                    digital_sub_name={digital_sub_name}
                    sub_title={`Add Additional Discipline  (Denied):`}
                />

                <div className="file-upload-group">
                    <div className="file-upload-group-item">
                        <FileUploadComponent />
                    </div>
                </div>
            </>
        )
    }
}

export default DisciplineHistoryDeniedForm
